import {
    AuthState,
    CreateProjectOptions,
    OpenSaveDialogOptions,
    DirectoryData,
    DirectoryRefreshCallback,
    DirectoryRefreshPayload,
    IPlatformApi,
    GitWorkspaceInfo,
    LayoutMode,
    LicenseState,
    LogMessage,
    MessageType,
    RecentProject,
    Result,
    SerializableMenuItem,
    ToolchainSetupProgress,
    License
} from '@lved/shared-core';

type FileSystemChangeCallback = (event: unknown, fileEvent: string, filePath: string) => void | Promise<void>;
type GlobalEventCallback = (eventId: string) => void;
type LogMessageCallback = (event: unknown, status: LogMessage) => void;
type ToolchainSetupProgressCallback = (event: unknown, progress: ToolchainSetupProgress) => void;
type BuildFlagValue = string | boolean | undefined;

export type TheiaApiDelegates = {
    getResourcesSourcePath?: () => string | Promise<string>;
    getResourcesPath?: () => Promise<string>;
    getFileData?: (filePath: string) => Promise<string>;
    getBinaryFileData?: (filePath: string) => Promise<Uint8Array>;
    getDirectoryData?: (dirPath: string) => Promise<DirectoryData | null>;
    pathIsDirectory?: (dirPath: string) => Promise<boolean>;
    saveFileData?: (filePath: string, content: string) => Promise<boolean>;
    writeBinaryFile?: (path: string, data: Uint8Array) => Promise<void>;
    delete?: (path: string) => Promise<void>;
    selectDirectory?: (projectPath?: string) => Promise<DirectoryData | null>;
    openDirectorySelector?: (defaultPath?: string) => Promise<string>;
    openSaveDialog?: (options?: OpenSaveDialogOptions) => Promise<string>;
    createProject?: (projectPath: string, options: CreateProjectOptions) => Promise<boolean>;
    getRecentProjects?: () => Promise<RecentProject[]>;
    getOpenedProject?: () => Promise<string | null>;
    saveOpenedProject?: (projectPath: string | null) => Promise<void>;
    shortenPath?: (path: string) => string;
    runContextMenuAction?: (action: string, path: string, name?: string) => Promise<Result>;
    openContextMenu?: (path: string) => Promise<Result>;
    showErrorMessageBox?: (title: string, message: string) => Promise<number>;
    showUnsavedFilesDialog?: (title: string, message: string) => Promise<number>;
    showConfirmDialog?: (
        title: string,
        message: string,
        confirmLabel?: string,
        cancelLabel?: string
    ) => Promise<boolean>;
    showNotification?: (message: string, type?: MessageType) => void;
    openExternal?: (url: string) => Promise<void>;
    fetch?: typeof fetch;
    generateProjectCode?: (projectPath: string) => Promise<Result>;
    compileProject?: (projectPath: string) => Promise<Result>;
    cleanProject?: (projectPath: string) => Promise<Result>;
    getGitRemotes?: (projectPath: string) => Promise<GitWorkspaceInfo | undefined>;
    isGitInstalled?: () => Promise<boolean>;
    // Backend-backed auth session storage (keeps the session secret out of renderer localStorage).
    getStoredAuthState?: () => Promise<AuthState>;
    saveStoredAuthState?: (authState: AuthState) => Promise<void>;
    clearStoredAuthState?: () => Promise<void>;
    executeCommand?: (commandId: string) => Promise<void>;
};

const unsupported = (method: string): Error => new Error(`TheiaApi.${method} is not implemented yet.`);

const unsupportedResult = (method: string): Result => ({
    success: false,
    error: unsupported(method).message
});

const emptyAuthState = (): AuthState => ({
    isAuthenticated: false,
    userEmail: undefined,
    userId: undefined,
    sessionId: undefined,
    expire: undefined,
    lastLoginTime: undefined
});

const THEIA_LICENSE_STORAGE_KEY = 'lved.theia.licenseState';
const E2E_LICENSE_ID = 'theia-electron-e2e-license';
const E2E_GRACE_PERIOD_MS = 24 * 60 * 60_000;

const isEnabledBuildFlag = (value: BuildFlagValue): boolean =>
    value === true || (typeof value === 'string' && value.toLowerCase() === 'true');

const isTheiaE2EAuthBypassBuild = (): boolean =>
    isEnabledBuildFlag(typeof process !== 'undefined' ? process.env.THEIA_E2E_AUTH_BYPASS : undefined);

const readJsonStorage = <T>(key: string, fallback: T): T => {
    if (typeof localStorage === 'undefined') {
        return fallback;
    }
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return fallback;
        return { ...fallback, ...JSON.parse(raw) } as T;
    } catch {
        return fallback;
    }
};

const writeJsonStorage = (key: string, value: unknown): void => {
    if (typeof localStorage === 'undefined') {
        return;
    }
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Quota or private mode — ignore.
    }
};

const emptyLicenseState = (): LicenseState => ({
    selectedLicense: {} as License,
    selectedConfirmedAt: undefined,
    inGracePeriodUntil: undefined,
    lastFetched: 0
});

const createE2ELicenseState = (): LicenseState => {
    const seededAt: number = Date.now();
    return {
        selectedLicense: {
            id: E2E_LICENSE_ID,
            displayName: 'Theia Electron E2E License',
            licenseTier: 'Platform',
            seatAvailable: true,
            expired: false
        },
        selectedConfirmedAt: seededAt,
        inGracePeriodUntil: seededAt + E2E_GRACE_PERIOD_MS,
        lastFetched: seededAt
    };
};

const readInitialLicenseState = (): LicenseState => {
    const licenseState: LicenseState = readJsonStorage(THEIA_LICENSE_STORAGE_KEY, emptyLicenseState());
    if (licenseState.selectedLicense?.id || !isTheiaE2EAuthBypassBuild()) {
        return licenseState;
    }
    return createE2ELicenseState();
};

export class TheiaApi implements IPlatformApi {
    devmode: boolean = false;
    type: 'theia' = 'theia';

    // Delegates allow injection of custom implementations for certain API methods.
    // Currently it is being initialized in LVED Theiai Platform API Contribution with implementations that use Theia services.
    private delegates: TheiaApiDelegates;
    private readonly fsCallbacks: Record<symbol, FileSystemChangeCallback> = {};
    private readonly refreshDirectoryCallbacks: Set<DirectoryRefreshCallback> = new Set<DirectoryRefreshCallback>();
    private readonly globalEventCallbacks: Record<symbol, GlobalEventCallback> = {};
    private readonly logMessageCallbacks: Record<symbol, LogMessageCallback> = {};
    private readonly toolchainSetupProgressCallbacks: Record<symbol, ToolchainSetupProgressCallback> = {};
    private authState: AuthState = emptyAuthState();
    private licenseState: LicenseState = emptyLicenseState();
    private layoutMode: LayoutMode = LayoutMode.CODE;
    private codeGenModes: string[] = ['C'];
    private codeGenMode: string = 'C';

    // Auth state lives in the Theia backend store (see auth-storage delegates), which is wired up
    // asynchronously via `setDelegates`. Gate auth reads/writes on this so an early `getAuthState`
    // (the eager `initializeAuth` at store import) waits for the delegates instead of losing the
    // persisted session. A timeout prevents hanging if delegates are never installed.
    private authStorageReady!: Promise<void>;
    private resolveAuthStorageReady!: () => void;
    private authHydrated: boolean = false;

    constructor(delegates: TheiaApiDelegates = {}) {
        this.delegates = delegates;
        this.authStorageReady = new Promise<void>((resolve) => {
            this.resolveAuthStorageReady = resolve;
        });
        setTimeout(() => this.resolveAuthStorageReady(), 8000);
        this.licenseState = readInitialLicenseState();
        if (this.hasAuthStorageDelegates()) this.resolveAuthStorageReady();
    }

    setDelegates(delegates: TheiaApiDelegates): void {
        this.delegates = delegates;
        if (this.hasAuthStorageDelegates()) this.resolveAuthStorageReady();
    }

    private hasAuthStorageDelegates(): boolean {
        return Boolean(
            this.delegates.getStoredAuthState &&
            this.delegates.saveStoredAuthState &&
            this.delegates.clearStoredAuthState
        );
    }

    private isBrowserOnlyMode(): boolean {
        return typeof window !== 'undefined' && Boolean(window.LVGL_THEIA_BROWSER_ONLY);
    }

    getResourcesSourcePath(): string | Promise<string> {
        if (this.delegates.getResourcesSourcePath) return this.delegates.getResourcesSourcePath();
        return Promise.resolve('');
    }

    async getResourcesPath(): Promise<string> {
        if (this.isBrowserOnlyMode()) return this.getResourcesSourcePath();
        if (this.delegates.getResourcesPath) return this.delegates.getResourcesPath();
        return Promise.resolve('');
    }

    async getFileData(filePath: string): Promise<string> {
        if (this.delegates.getFileData) return this.delegates.getFileData(filePath);
        throw unsupported('getFileData');
    }

    async getBinaryFileData(filePath: string): Promise<Uint8Array> {
        if (this.delegates.getBinaryFileData) return this.delegates.getBinaryFileData(filePath);
        throw unsupported('getBinaryFileData');
    }

    async writeBinaryFile(path: string, data: Uint8Array): Promise<void> {
        if (this.delegates.writeBinaryFile) return this.delegates.writeBinaryFile(path, data);
        throw unsupported('writeBinaryFile');
    }

    async delete(path: string): Promise<void> {
        if (this.delegates.delete) return this.delegates.delete(path);
        throw unsupported('delete');
    }

    async selectDirectory(projectPath?: string): Promise<DirectoryData | null> {
        if (this.delegates.selectDirectory) return this.delegates.selectDirectory(projectPath);
        return Promise.resolve(null);
    }

    async openDirectorySelector(defaultPath?: string): Promise<string> {
        if (this.delegates.openDirectorySelector) return this.delegates.openDirectorySelector(defaultPath);
        return Promise.resolve('');
    }

    async openSaveDialog(options?: OpenSaveDialogOptions): Promise<string> {
        if (this.delegates.openSaveDialog) return this.delegates.openSaveDialog(options);
        return Promise.resolve('');
    }

    onDirectorySelected(_callback: (event: unknown, data: DirectoryData) => void): void {}

    removeDirectorySelectedListener(): void {}

    onRefreshDirectory(callback: DirectoryRefreshCallback): void {
        this.refreshDirectoryCallbacks.add(callback);
    }

    removeRefreshDirectoryListener(callback?: DirectoryRefreshCallback): void {
        if (callback) {
            this.refreshDirectoryCallbacks.delete(callback);
            return;
        }
        this.refreshDirectoryCallbacks.clear();
    }

    onStartFNInteraction(
        _callback: (event: unknown, action: string, filePath: string, fileName: string) => void
    ): void {}

    removeStartFNInteraction(): void {}

    openTabContextMenu(_filePath: string): void {}

    onTabContextMenuAction(_callback: (event: unknown, action: string, filePath: string) => void): void {}

    removeTabContextMenuActionListener(): void {}

    async saveFileData(filePath: string, content: string): Promise<boolean> {
        if (this.delegates.saveFileData) return this.delegates.saveFileData(filePath, content);
        throw unsupported('saveFileData');
    }

    async generateProjectCode(projectPath: string): Promise<Result> {
        if (this.delegates.generateProjectCode) return this.delegates.generateProjectCode(projectPath);
        return unsupportedResult('generateProjectCode');
    }

    async generateWidget(
        _xmlPath: string,
        _widgetName: string,
        _outputPath: string,
        _projectPath: string
    ): Promise<Result> {
        return unsupportedResult('generateWidget');
    }

    async generateComponent(
        _xmlPath: string,
        _componentName: string,
        _outputPath: string,
        _projectPath: string
    ): Promise<Result> {
        return unsupportedResult('generateComponent');
    }

    async getDirectoryData(dirPath: string): Promise<DirectoryData | null> {
        if (this.delegates.getDirectoryData) return this.delegates.getDirectoryData(dirPath);
        throw unsupported('getDirectoryData');
    }

    async pathIsDirectory(dirPath: string): Promise<boolean> {
        if (this.delegates.pathIsDirectory) return this.delegates.pathIsDirectory(dirPath);
        throw unsupported('pathIsDirectory');
    }

    onLogMessage(callback: (event: unknown, status: LogMessage) => void): symbol {
        const callbackId: symbol = Symbol('theia-log-message');
        this.logMessageCallbacks[callbackId] = callback;
        return callbackId;
    }

    removeOnLogMessageListener(callbackId?: symbol): void {
        if (callbackId) delete this.logMessageCallbacks[callbackId];
        else
            Object.getOwnPropertySymbols(this.logMessageCallbacks).forEach(
                (key) => delete this.logMessageCallbacks[key]
            );
    }

    onLogMessageRemoveListener(_callback: (event: unknown, status: LogMessage) => void): void {}

    removeLogMessageRemoveListener(): void {}

    onToolchainSetupProgress(callback: (event: unknown, progress: ToolchainSetupProgress) => void): symbol {
        const callbackId: symbol = Symbol('theia-toolchain-setup-progress');
        this.toolchainSetupProgressCallbacks[callbackId] = callback;
        return callbackId;
    }

    removeToolchainSetupProgressListener(callbackId?: symbol): void {
        if (callbackId) delete this.toolchainSetupProgressCallbacks[callbackId];
        else
            Object.getOwnPropertySymbols(this.toolchainSetupProgressCallbacks).forEach(
                (key) => delete this.toolchainSetupProgressCallbacks[key]
            );
    }

    emitToolchainSetupProgress(progress: ToolchainSetupProgress): void {
        Object.getOwnPropertySymbols(this.toolchainSetupProgressCallbacks).forEach((callbackId) => {
            this.toolchainSetupProgressCallbacks[callbackId]?.(undefined, progress);
        });
    }

    async createProject(projectPath: string, options: CreateProjectOptions): Promise<boolean> {
        if (this.delegates.createProject) return this.delegates.createProject(projectPath, options);
        return false;
    }

    async compileProject(projectPath: string): Promise<Result> {
        if (this.delegates.compileProject) return this.delegates.compileProject(projectPath);
        return unsupportedResult('compileProject');
    }

    async getRecentProjects(): Promise<RecentProject[]> {
        if (this.delegates.getRecentProjects) return this.delegates.getRecentProjects();
        return [];
    }

    async addRecentProject(_projectPath: string, _currentTabs: string[]): Promise<string | null> {
        return null;
    }

    async removeRecentProject(_projectPath: string): Promise<void> {
        return;
    }

    onRecentProjects(_callback: (event: unknown, projects: RecentProject[]) => void): void {}

    removeRecentProjectsListener(): void {}

    shortenPath(path: string): string {
        if (this.delegates.shortenPath) return this.delegates.shortenPath(path);
        return path.split('/').pop() ?? path;
    }

    async getOpenedProject(): Promise<string | null> {
        if (this.delegates.getOpenedProject) return this.delegates.getOpenedProject();
        return null;
    }

    async saveOpenedProject(projectPath: string | null): Promise<void> {
        if (this.delegates.saveOpenedProject) return this.delegates.saveOpenedProject(projectPath);
    }

    async saveLayoutMode(layoutMode: LayoutMode): Promise<void> {
        this.layoutMode = layoutMode;
    }

    async getLayoutMode(): Promise<LayoutMode> {
        return this.layoutMode;
    }

    async getCodeGenModes(): Promise<string[]> {
        return this.codeGenModes;
    }

    async getCodeGenMode(): Promise<string> {
        return this.codeGenMode;
    }

    async setCodeGenMode(mode: string): Promise<void> {
        this.codeGenMode = mode;
    }

    async copyComponentLibraryToProject(
        _projectPath: string,
        _resourcesSrcPath: string,
        _libraryName: string
    ): Promise<void> {}

    async clearUserDataCache(): Promise<Result> {
        return unsupportedResult('clearUserDataCache');
    }

    async cleanProject(projectPath: string): Promise<Result> {
        if (this.delegates.cleanProject) return this.delegates.cleanProject(projectPath);
        return unsupportedResult('cleanProject');
    }

    async getGitRemotes(projectPath: string): Promise<GitWorkspaceInfo | undefined> {
        if (this.delegates.getGitRemotes) return this.delegates.getGitRemotes(projectPath);
        throw unsupported('getGitRemotes');
    }

    async isGitInstalled(): Promise<boolean> {
        if (this.delegates.isGitInstalled) return this.delegates.isGitInstalled();
        throw unsupported('isGitInstalled');
    }

    async openContextMenu(path: string): Promise<Result> {
        if (this.delegates.openContextMenu) return this.delegates.openContextMenu(path);
        return unsupportedResult('openContextMenu');
    }

    async runContextMenuAction(action: string, path: string, name?: string): Promise<Result> {
        if (this.delegates.runContextMenuAction) return this.delegates.runContextMenuAction(action, path, name);
        return unsupportedResult('runContextMenuAction');
    }

    onFileSystemChange(callback: FileSystemChangeCallback, callbackId: symbol): void {
        this.fsCallbacks[callbackId] = callback;
    }

    removeFileSystemChangeListener(callbackId: symbol): void {
        delete this.fsCallbacks[callbackId];
    }

    emitFileSystemChange(fileEvent: string, filePath: string): void {
        console.debug(`[TheiaApi] emitFileSystemChange: ${filePath} (${fileEvent})`);
        Object.getOwnPropertySymbols(this.fsCallbacks).forEach((callbackId) => {
            const callback: FileSystemChangeCallback | undefined = this.fsCallbacks[callbackId];
            if (!callback) return;

            try {
                // File-system listeners often read files asynchronously; isolate failures so one
                // stale OPFS event cannot surface as a global unhandled promise rejection.
                void Promise.resolve(callback(undefined, fileEvent, filePath)).catch((error: unknown): void => {
                    console.debug('[TheiaApi] File system change listener failed.', error);
                });
            } catch (error: unknown) {
                console.debug('[TheiaApi] File system change listener failed.', error);
            }
        });
    }

    emitRefreshDirectory(files: DirectoryRefreshPayload): void {
        console.debug(`[TheiaApi] emitRefreshDirectory`);
        this.refreshDirectoryCallbacks.forEach((callback: DirectoryRefreshCallback) => callback(undefined, files));
    }

    onAppCloseRequest(_callback: () => void): void {}

    removeAppCloseRequestListener(): void {}

    sendAppCloseResponse(): void {}

    async showErrorMessageBox(title: string, message: string): Promise<number> {
        if (this.delegates.showErrorMessageBox) return this.delegates.showErrorMessageBox(title, message);
        console.error(`${title}: ${message}`);
        return 0;
    }

    async showUnsavedFilesDialog(title: string, message: string): Promise<number> {
        if (this.delegates.showUnsavedFilesDialog) return this.delegates.showUnsavedFilesDialog(title, message);
        console.warn(`${title}: ${message}`);
        return 1;
    }

    async showConfirmDialog(
        title: string,
        message: string,
        confirmLabel?: string,
        cancelLabel?: string
    ): Promise<boolean> {
        if (this.delegates.showConfirmDialog)
            return this.delegates.showConfirmDialog(title, message, confirmLabel, cancelLabel);
        if (typeof window !== 'undefined' && typeof window.confirm === 'function') return window.confirm(message);
        console.warn(`${title}: ${message}`);
        return false;
    }

    showNotification(message: string, type: MessageType = MessageType.DEFAULT): void {
        if (this.delegates.showNotification) {
            this.delegates.showNotification(message, type);
            return;
        }
        if (type === MessageType.ERROR) {
            console.error(message);
            return;
        }
        if (type === MessageType.WARNING) {
            console.warn(message);
            return;
        }
        console.info(message);
    }

    async updateMenu(): Promise<void> {}

    async openExternal(url: string): Promise<void> {
        if (this.delegates.openExternal) return this.delegates.openExternal(url);
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    async getMenuStructure(): Promise<SerializableMenuItem[]> {
        return [];
    }

    async handleMenuItemClick(_menuId: string): Promise<void> {}

    sendGlobalEvent(eventId: string): void {
        Object.getOwnPropertySymbols(this.globalEventCallbacks).forEach((callbackId) => {
            this.globalEventCallbacks[callbackId]?.(eventId);
        });
    }

    onGlobalEvent(callback: GlobalEventCallback, callbackId: symbol): void {
        this.globalEventCallbacks[callbackId] = callback;
    }

    removeGlobalEventListener(callbackId: symbol): void {
        delete this.globalEventCallbacks[callbackId];
    }

    async saveAuthState(authState: Partial<AuthState>): Promise<void> {
        this.authState = { ...this.authState, ...authState };
        this.authHydrated = true;
        await this.authStorageReady;
        if (this.delegates.saveStoredAuthState) {
            try {
                await this.delegates.saveStoredAuthState(this.authState);
            } catch (error: unknown) {
                console.warn('[TheiaApi] Failed to persist auth state', error);
            }
        }
    }

    async getAuthState(): Promise<AuthState> {
        if (!this.authHydrated) {
            await this.authStorageReady;
            if (this.delegates.getStoredAuthState) {
                try {
                    this.authState = { ...emptyAuthState(), ...(await this.delegates.getStoredAuthState()) };
                } catch (error: unknown) {
                    console.warn('[TheiaApi] Failed to load auth state', error);
                }
            }
            this.authHydrated = true;
        }
        return this.authState;
    }

    async clearAuthState(): Promise<void> {
        this.authState = emptyAuthState();
        this.authHydrated = true;
        await this.authStorageReady;
        if (this.delegates.clearStoredAuthState) {
            try {
                await this.delegates.clearStoredAuthState();
            } catch (error: unknown) {
                console.warn('[TheiaApi] Failed to clear auth state', error);
            }
        }
    }

    async saveLicenseState(licenseState: Partial<LicenseState>): Promise<void> {
        this.licenseState = { ...this.licenseState, ...licenseState };
        writeJsonStorage(THEIA_LICENSE_STORAGE_KEY, this.licenseState);
    }

    async getLicenseState(): Promise<LicenseState> {
        return this.licenseState;
    }

    async clearLicenseState(): Promise<void> {
        this.licenseState = emptyLicenseState();
        if (typeof localStorage !== 'undefined') {
            try {
                localStorage.removeItem(THEIA_LICENSE_STORAGE_KEY);
            } catch {
                // ignore
            }
        }
    }

    async fetch(...args: Parameters<typeof fetch>): ReturnType<typeof fetch> {
        const fetchImpl: typeof fetch = this.delegates.fetch ?? fetch;
        return fetchImpl(...args);
    }

    async executeCommand(commandId: string): Promise<void> {
        if (this.delegates.executeCommand) {
            await this.delegates.executeCommand(commandId);
            return;
        }
        throw unsupported('executeCommand');
    }
}
